import { Typography } from '@healthinal/ui';
import {
  LabReportDetectionMethodDto,
  LabReportDetectionMethodDtoMethod,
  LabReportDetectionMethodDtoTestResult,
  LabReportDto,
  LabReportDtoLabTestMaterial,
  LabReportDtoSpecies,
  LabReportMutationDtoEnzyme,
  LabReportMutationDtoMutationStatus,
  LabReportResistanceDtoResistance,
  LabReportResistanceDtoResistanceName,
} from '../api/generated/model';

export function getTestTypeName(type: LabReportDtoLabTestMaterial | null) {
  switch (type) {
    case LabReportDtoLabTestMaterial.SPUTUM_FROM_THE_AIRWAYS:
      return 'Sputum';
    case LabReportDtoLabTestMaterial.OTHER_MATERIAL_FROM_THE_AIRWAYS:
      return 'Anderes Material aus den Luftwegen';
    case LabReportDtoLabTestMaterial.OTHER_MATERIAL:
      return 'Anderes Material';
  }
}

export function getDetectionMethodName(method: LabReportDetectionMethodDtoMethod) {
  switch (method) {
    case LabReportDetectionMethodDtoMethod.MICROSCOPIC_DIRECT_DETECTION:
      return 'Mikroskopie';
    case LabReportDetectionMethodDtoMethod.MOLECULAR_BIOLOGICAL_DIRECT_DETECTION:
      return 'PCR';
    case LabReportDetectionMethodDtoMethod.CULTURE:
      return 'Kultur';
  }
}

export function getTestResultName(result: LabReportDetectionMethodDtoTestResult) {
  switch (result) {
    case LabReportDetectionMethodDtoTestResult.POSITIVE:
      return 'Positiv';
    case LabReportDetectionMethodDtoTestResult.NEGATIVE:
      return 'Negativ';
    case LabReportDetectionMethodDtoTestResult.PENDING:
      return 'Ausstehend';
    case LabReportDetectionMethodDtoTestResult.NOT_EXECUTED:
      return 'Nicht gemacht';
  }
}

export function getDetectionMethodSummaryStrings(
  detectionMethods?: LabReportDetectionMethodDto[] | null,
): string[] {
  if (!detectionMethods?.length) return [];

  return detectionMethods.map(
    (item) => `${getDetectionMethodName(item.method)} | ${getTestResultName(item.testResult)}`,
  );
}

export function getLabReportMaterialName(report?: Pick<LabReportDto, 'labTestMaterial' | 'otherMaterial'> | null) {
  if (!report) return '';

  return report.labTestMaterial === LabReportDtoLabTestMaterial.SPUTUM_FROM_THE_AIRWAYS
    ? (getTestTypeName(report.labTestMaterial) ?? '')
    : (report.otherMaterial ?? '');
}

export function getDetectionMethodWithMaterialSummaryStrings(
  report?: Pick<LabReportDto, 'labTestMaterial' | 'otherMaterial' | 'detectionMethods'> | null,
): string[] {
  if (!report?.detectionMethods?.length) return [];

  const material = getLabReportMaterialName(report);

  return report.detectionMethods.map(
    (item) => `${material} | ${getDetectionMethodName(item.method)} | ${getTestResultName(item.testResult)}`,
  );
}

export function getSpeciesName(species: LabReportDtoSpecies) {
  switch (species) {
    case LabReportDtoSpecies.M_TUBERCULOSIS:
      return <Typography fontStyle="italic">M. tuberculosis</Typography>;
    case LabReportDtoSpecies.M_BOVIS:
      return <Typography fontStyle="italic">M. bovis</Typography>;
    case LabReportDtoSpecies.M_BOVIS_BCG:
      return (
        <Typography>
          <Typography fontStyle="italic">M. bovis</Typography> BCG
        </Typography>
      );
    case LabReportDtoSpecies.M_CAPRAE:
      return <Typography fontStyle="italic">M. caprae</Typography>;
    case LabReportDtoSpecies.M_AFRICANUM:
      return <Typography fontStyle="italic">M. africanum</Typography>;
    case LabReportDtoSpecies.M_TUBERCULOSIS_COMPLEX:
      return (
        <Typography>
          <Typography fontStyle="italic">M. tuberculosis</Typography>-Komplex
        </Typography>
      );
  }
}
export function getSpeciesNameString(species: LabReportDtoSpecies) {
  switch (species) {
    case LabReportDtoSpecies.M_TUBERCULOSIS:
      return 'M. tuberculosis';
    case LabReportDtoSpecies.M_BOVIS:
      return 'M. bovis';
    case LabReportDtoSpecies.M_BOVIS_BCG:
      return 'M. bovis BCG';
    case LabReportDtoSpecies.M_CAPRAE:
      return 'M. caprae';
    case LabReportDtoSpecies.M_AFRICANUM:
      return 'M. africanum';
    case LabReportDtoSpecies.M_TUBERCULOSIS_COMPLEX:
      return 'M. tuberculosis-Komplex';
  }
}

export function getResistanceNameName(name: LabReportResistanceDtoResistanceName) {
  switch (name) {
    case LabReportResistanceDtoResistanceName.RIFAMPICIN:
      return 'Rifampicin';
    case LabReportResistanceDtoResistanceName.PYRAZINAMID:
      return 'Pyrazinamid';
    case LabReportResistanceDtoResistanceName.ETHAMBUTOL:
      return 'Ethambutol';
    case LabReportResistanceDtoResistanceName.ISONIAZID:
      return 'Isoniazid';
  }
}

export function getResistanceName(resistance: LabReportResistanceDtoResistance) {
  switch (resistance) {
    case LabReportResistanceDtoResistance.SENSIBLE:
      return 'Sensibel';
    case LabReportResistanceDtoResistance.RESISTENT:
      return 'Resistent';
    case LabReportResistanceDtoResistance.INTERMEDIATE:
      return 'Intermediär';
  }
}

export function getEnzymeName(enzyme: LabReportMutationDtoEnzyme) {
  switch (enzyme) {
    case LabReportMutationDtoEnzyme.KATG:
      return 'katG';
    case LabReportMutationDtoEnzyme.INHA_PROMOTER:
      return 'inhA-Promoter';
    case LabReportMutationDtoEnzyme.RPOB:
      return 'rpoB';
  }
}

export function getMutationStatusName(status: LabReportMutationDtoMutationStatus): string {
  switch (status) {
    case LabReportMutationDtoMutationStatus.YES:
      return 'Ja';
    case LabReportMutationDtoMutationStatus.WILDTYPE:
      return 'Wildtyp';
    case LabReportMutationDtoMutationStatus.NOT_MADE:
      return 'Nicht gemacht';
  }
}
